import { useState, useEffect, useRef, useCallback } from 'react';
import { projectsApi } from '../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Camera, Save, Trash2, Upload, ChevronLeft, ChevronRight,
  FileText, ImageIcon, Loader2, ZoomIn, X, CheckCircle2, Archive,
  File as FileIcon, Download, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import { usePhotoBlobs } from '../hooks/usePhotoBlobs';
import { getFileUrl } from '../lib/api';

// ---- Sub-components ----

function StageSection({ project, onMoveStage, onMarkComplete }) {
  const stages = project.stages || [];
  const currentStage = stages.find(s => s.id === project.current_stage_id);
  const currentIndex = stages.findIndex(s => s.id === project.current_stage_id);
  const isLastStage = currentIndex >= stages.length - 1;

  return (
    <div className="bg-[#23272E] border border-[#33383F] rounded-xl p-4">
      <p className="accent-text text-[#9CA3AF] mb-3">CURRENT STAGE</p>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: currentStage?.color || '#2C4C3B' }} />
          <span className="font-bold text-[#F0EDE8] text-lg tracking-tight">{currentStage?.name || 'Unknown'}</span>
        </div>
        <Badge variant="secondary" className="bg-[#2C3038] text-[#9CA3AF] rounded-xl">
          {currentIndex + 1} / {stages.length}
        </Badge>
      </div>
      <div className="flex gap-1 mb-3">
        {stages.map((s, i) => (
          <div key={s.id} className="h-2 flex-1 rounded-full transition-all"
            style={{ backgroundColor: i <= currentIndex ? s.color : '#DCD5C6' }} />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" disabled={currentIndex <= 0}
          onClick={() => onMoveStage('prev')}
          className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] disabled:opacity-30 rounded-xl"
          data-testid="detail-move-prev">
          <ChevronLeft className="w-4 h-4 mr-1" />Back
        </Button>
        {isLastStage ? (
          <Button size="sm" onClick={onMarkComplete}
            className="bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
            data-testid="mark-complete-button">
            <CheckCircle2 className="w-4 h-4 mr-1" />Mark Complete
          </Button>
        ) : (
          <Button variant="outline" size="sm"
            onClick={() => onMoveStage('next')}
            className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
            data-testid="detail-move-next">
            Next<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

function NotesSection({ notes, onChange, onSave, saving }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#2C4C3B]" />
        <p className="accent-text text-[#9CA3AF]">NOTES</p>
      </div>
      <Textarea value={notes} onChange={onChange}
        placeholder="Add project notes here... materials used, client preferences, measurements..."
        className="bg-[#1A1D21] border-[#33383F] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl min-h-[120px] text-[#F0EDE8]"
        data-testid="project-notes-textarea" />
      <Button onClick={onSave} disabled={saving}
        className="mt-2 bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl" data-testid="save-notes-button">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save Notes
      </Button>
    </div>
  );
}

function PhotoCard({ photo, blobUrl, onPreview, onDelete, captionEdit, setCaptionEdit, onSaveCaption }) {
  const isEditing = captionEdit.id === photo.id;
  return (
    <div className="bg-[#1A1D21] border border-[#33383F] rounded-xl overflow-hidden group relative" data-testid={`photo-card-${photo.id}`}>
      <div className="aspect-square relative bg-[#1A1D21] cursor-pointer" onClick={() => onPreview(photo)}>
        {blobUrl ? (
          <img src={blobUrl} alt={photo.caption || photo.original_filename} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#DCD5C6]" /></div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      <div className="p-2">
        {isEditing ? (
          <div className="flex gap-1">
            <Input value={captionEdit.value} onChange={(e) => setCaptionEdit({ ...captionEdit, value: e.target.value })}
              className="h-7 text-xs bg-[#1A1D21] border-[#33383F] rounded-xl" placeholder="Add caption..."
              data-testid={`caption-input-${photo.id}`} onKeyDown={(e) => e.key === 'Enter' && onSaveCaption(photo.id)} />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onSaveCaption(photo.id)}
              data-testid={`save-caption-${photo.id}`}><Save className="w-3 h-3" /></Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <button onClick={() => setCaptionEdit({ id: photo.id, value: photo.caption || '' })}
              className="text-xs text-[#9CA3AF] hover:text-[#F0EDE8] truncate text-left flex-1"
              data-testid={`edit-caption-${photo.id}`}>{photo.caption || 'Add caption...'}</button>
            <Button size="icon" variant="ghost"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 text-[#DC2626] hover:text-[#DC2626] hover:bg-red-50 flex-shrink-0"
              onClick={() => onDelete(photo.id)} data-testid={`delete-photo-${photo.id}`}><Trash2 className="w-3 h-3" /></Button>
          </div>
        )}
        <p className="text-xs text-[#9CA3AF] mt-1 font-mono">
          {photo.uploaded_by_name} &middot; {new Date(photo.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

function PhotoGallery({ photos, photoBlobs, uploading, onUpload, onDelete, onPreview, captionEdit, setCaptionEdit, onSaveCaption, fileInputRef }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[#2C4C3B]" />
          <p className="accent-text text-[#9CA3AF]">PHOTOS</p>
          <Badge variant="secondary" className="bg-[#2C3038] text-[#9CA3AF] rounded-xl ml-1">{photos.length}</Badge>
        </div>
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="photo-file-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" size="sm"
            className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl" data-testid="upload-photo-button">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Add Photos'}
          </Button>
        </div>
      </div>
      {photos.length === 0 ? (
        <div className="border-2 border-dashed border-[#33383F] rounded-xl p-8 text-center cursor-pointer hover:border-[#4A9D6E] hover:bg-[#23272E] transition-colors"
          onClick={() => fileInputRef.current?.click()} data-testid="photo-dropzone">
          <Camera className="w-10 h-10 text-[#DCD5C6] mx-auto mb-3" />
          <p className="text-[#9CA3AF] font-medium">No photos yet</p>
          <p className="text-sm text-[#9CA3AF]">Click to upload project photos</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((photo) => (
            <PhotoCard key={photo.id} photo={photo} blobUrl={photoBlobs[photo.id]}
              onPreview={onPreview} onDelete={onDelete}
              captionEdit={captionEdit} setCaptionEdit={setCaptionEdit} onSaveCaption={onSaveCaption} />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoPreviewDialog({ photo, blobUrl, onClose }) {
  return (
    <Dialog open={!!photo} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A1A1A] border-[#333] rounded-xl max-w-3xl p-2">
        <DialogHeader className="sr-only"><DialogTitle>Photo Preview</DialogTitle></DialogHeader>
        {photo && blobUrl && (
          <div>
            <img src={blobUrl} alt={photo.caption || photo.original_filename}
              className="w-full max-h-[70vh] object-contain rounded-xl" data-testid="photo-preview-image" />
            {photo.caption && <p className="text-white text-sm text-center mt-2 px-4">{photo.caption}</p>}
            <p className="text-[#9CA3AF] text-xs text-center mt-1 font-mono">
              {photo.uploaded_by_name} &middot; {new Date(photo.created_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return ImageIcon;
  if (contentType?.includes('pdf')) return FileText;
  return FileIcon;
}

function FilesSection({ files, uploading, onUpload, onDelete, onDownload, fileInputRef }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#4A9D6E]" />
          <p className="accent-text text-[#9CA3AF]">FILES</p>
          <Badge variant="secondary" className="bg-[#2C3038] text-[#9CA3AF] rounded-xl ml-1">{files.length}</Badge>
        </div>
        <div>
          <input ref={fileInputRef} type="file" multiple onChange={onUpload} className="hidden" data-testid="file-upload-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            variant="outline" size="sm"
            className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
            data-testid="upload-file-button">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Add Files'}
          </Button>
        </div>
      </div>

      {files.length === 0 ? (
        <div className="border-2 border-dashed border-[#33383F] rounded-xl p-6 text-center cursor-pointer hover:border-[#4A9D6E] hover:bg-[#23272E] transition-colors"
          onClick={() => fileInputRef.current?.click()} data-testid="file-dropzone">
          <Paperclip className="w-8 h-8 text-[#33383F] mx-auto mb-2" />
          <p className="text-[#9CA3AF] font-medium text-sm">No files yet</p>
          <p className="text-xs text-[#555D68]">Click to upload documents, PDFs, spreadsheets...</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => {
            const Icon = getFileIcon(file.content_type);
            return (
              <div key={file.id}
                className="flex items-center gap-3 bg-[#23272E] border border-[#33383F] rounded-xl px-3 py-2.5 group"
                data-testid={`file-item-${file.id}`}>
                <Icon className="w-5 h-5 text-[#4A9D6E] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#F0EDE8] font-medium truncate">{file.original_filename}</p>
                  <p className="text-xs text-[#555D68]">
                    {formatFileSize(file.size)} &middot; {file.uploaded_by_name} &middot; {new Date(file.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Button size="icon" variant="ghost"
                  className="h-7 w-7 text-[#9CA3AF] hover:text-[#4A9D6E] hover:bg-[#2C3038] rounded-lg"
                  onClick={() => onDownload(file)} data-testid={`download-file-${file.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost"
                  className="h-7 w-7 text-[#9CA3AF] hover:text-[#E55B5B] hover:bg-[#E55B5B]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onDelete(file.id)} data-testid={`delete-file-${file.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Main Component ----

export default function ProjectDetailPanel({ project, open, onClose, onUpdate }) {
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [captionEdit, setCaptionEdit] = useState({ id: null, value: '' });
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const { photoBlobs, removeBlob } = usePhotoBlobs(photos);

  // Editable project info
  const [editTitle, setEditTitle] = useState('');
  const [editClient, setEditClient] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTargetDate, setEditTargetDate] = useState('');
  const [infoChanged, setInfoChanged] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);

  useEffect(() => {
    if (project) {
      setNotes(project.notes || '');
      setPhotos(project.photos || []);
      setFiles(project.files || []);
      setEditTitle(project.title || '');
      setEditClient(project.client_name || '');
      setEditDescription(project.description || '');
      setEditTargetDate(project.target_completion_date || '');
      setInfoChanged(false);
    }
  }, [project]);

  // Track changes to project info fields
  const handleInfoChange = useCallback((field, value) => {
    if (field === 'title') setEditTitle(value);
    if (field === 'client') setEditClient(value);
    if (field === 'description') setEditDescription(value);
    if (field === 'target_date') setEditTargetDate(value);
    setInfoChanged(true);
  }, []);

  const handleSaveInfo = useCallback(async () => {
    if (!project) return;
    setSavingInfo(true);
    try {
      await projectsApi.update(project.id, {
        title: editTitle,
        client_name: editClient,
        description: editDescription,
        target_completion_date: editTargetDate || null,
      });
      toast.success('Project updated');
      setInfoChanged(false);
      onUpdate();
    } catch (err) {
      toast.error('Failed to save changes');
      void err;
    } finally {
      setSavingInfo(false);
    }
  }, [project, editTitle, editClient, editDescription, editTargetDate, onUpdate]);

  const handleCancelInfo = useCallback(() => {
    if (!project) return;
    setEditTitle(project.title || '');
    setEditClient(project.client_name || '');
    setEditDescription(project.description || '');
    setEditTargetDate(project.target_completion_date || '');
    setInfoChanged(false);
  }, [project]);

  const handleSaveNotes = useCallback(async () => {
    if (!project) return;
    setSavingNotes(true);
    try {
      await projectsApi.update(project.id, { notes });
      toast.success('Notes saved');
      onUpdate();
    } catch { toast.error('Failed to save notes'); }
    finally { setSavingNotes(false); }
  }, [project, notes, onUpdate]);

  const handleUploadPhoto = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !project) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await projectsApi.uploadPhoto(project.id, file, '');
        setPhotos(prev => [...prev, res.data]);
      }
      toast.success(`${files.length} photo(s) uploaded`);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [project, onUpdate]);

  const handleDeletePhoto = useCallback(async (photoId) => {
    if (!project) return;
    try {
      await projectsApi.deletePhoto(project.id, photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      removeBlob(photoId);
      toast.success('Photo removed');
      onUpdate();
    } catch { toast.error('Failed to remove photo'); }
  }, [project, onUpdate, removeBlob]);

  const handleSaveCaption = useCallback(async (photoId) => {
    if (!project) return;
    try {
      await projectsApi.updatePhotoCaption(project.id, photoId, captionEdit.value);
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption: captionEdit.value } : p));
      setCaptionEdit({ id: null, value: '' });
      toast.success('Caption updated');
    } catch { toast.error('Failed to update caption'); }
  }, [project, captionEdit.value]);

  const handleMoveStage = useCallback(async (direction) => {
    if (!project) return;
    const stagesList = project.stages || [];
    const idx = stagesList.findIndex(s => s.id === project.current_stage_id);
    const newIdx = direction === 'next' ? idx + 1 : idx - 1;
    if (newIdx < 0 || newIdx >= stagesList.length) return;
    try {
      await projectsApi.update(project.id, { current_stage_id: stagesList[newIdx].id });
      toast.success(`Moved to ${stagesList[newIdx].name}`);
      onUpdate();
    } catch { toast.error('Failed to update stage'); }
  }, [project, onUpdate]);

  const handleMarkComplete = useCallback(async () => {
    if (!project) return;
    if (!window.confirm('Mark this project as complete and archive it?')) return;
    try {
      await projectsApi.archive(project.id);
      toast.success('Project marked complete and archived');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error('Failed to archive project');
      void err;
    }
  }, [project, onUpdate, onClose]);

  const handleUploadFile = useCallback(async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0 || !project) return;
    setUploadingFiles(true);
    try {
      for (const f of fileList) {
        const res = await projectsApi.uploadFile(project.id, f);
        setFiles(prev => [...prev, res.data]);
      }
      toast.success(`${fileList.length} file(s) uploaded`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'File upload failed');
      void err;
    } finally {
      setUploadingFiles(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  }, [project, onUpdate]);

  const handleDeleteFile = useCallback(async (fileId) => {
    if (!project) return;
    try {
      await projectsApi.deleteFile(project.id, fileId);
      setFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('File removed');
      onUpdate();
    } catch (err) {
      toast.error('Failed to remove file');
      void err;
    }
  }, [project, onUpdate]);

  const handleDownloadFile = useCallback(async (file) => {
    try {
      const url = getFileUrl(file.storage_path);
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error('Failed to download file');
      void err;
    }
  }, []);

  if (!project) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right"
          className="bg-[#1A1D21] border-l border-[#33383F] w-full sm:max-w-xl p-0 overflow-hidden"
          data-testid="project-detail-panel">
          <SheetHeader className="sr-only">
            <SheetTitle>{project.title}</SheetTitle>
            <SheetDescription>Project details for {project.title}</SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-full">
            <div className="p-6 space-y-6">
              {/* Editable Project Info */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="accent-text text-[#9CA3AF]">CLIENT</label>
                  <Input value={editClient}
                    onChange={(e) => handleInfoChange('client', e.target.value)}
                    className="bg-[#1A1D21] border-[#33383F] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl font-medium"
                    data-testid="detail-client-input" />
                </div>
                <div className="space-y-1">
                  <label className="accent-text text-[#9CA3AF]">PROJECT TITLE</label>
                  <Input value={editTitle}
                    onChange={(e) => handleInfoChange('title', e.target.value)}
                    className="bg-[#1A1D21] border-[#33383F] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl text-lg font-bold tracking-tight"
                    data-testid="detail-title-input" />
                </div>
                <div className="space-y-1">
                  <label className="accent-text text-[#9CA3AF]">DESCRIPTION</label>
                  <Textarea value={editDescription}
                    onChange={(e) => handleInfoChange('description', e.target.value)}
                    placeholder="Project description..."
                    className="bg-[#1A1D21] border-[#33383F] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl min-h-[60px] text-[#9CA3AF]"
                    data-testid="detail-description-input" />
                </div>
                <div className="space-y-1">
                  <label className="accent-text text-[#9CA3AF]">TARGET COMPLETION DATE</label>
                  <Input type="date" value={editTargetDate}
                    onChange={(e) => handleInfoChange('target_date', e.target.value)}
                    className="bg-[#1A1D21] border-[#33383F] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
                    data-testid="detail-target-date-input" />
                </div>
                {/* Save / Cancel buttons — only show when info has changed */}
                {infoChanged && (
                  <div className="flex gap-2 pt-1">
                    <Button onClick={handleSaveInfo} disabled={savingInfo}
                      className="bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
                      data-testid="detail-save-button">
                      {savingInfo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                    <Button variant="outline" onClick={handleCancelInfo}
                      className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
                      data-testid="detail-cancel-button">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <StageSection project={project} onMoveStage={handleMoveStage} onMarkComplete={handleMarkComplete} />
              <Separator className="bg-[#DCD5C6]" />
              <NotesSection notes={notes} onChange={(e) => setNotes(e.target.value)} onSave={handleSaveNotes} saving={savingNotes} />
              <Separator className="bg-[#DCD5C6]" />
              <PhotoGallery photos={photos} photoBlobs={photoBlobs} uploading={uploading}
                onUpload={handleUploadPhoto} onDelete={handleDeletePhoto} onPreview={setPreviewPhoto}
                captionEdit={captionEdit} setCaptionEdit={setCaptionEdit} onSaveCaption={handleSaveCaption}
                fileInputRef={fileInputRef} />

              <Separator className="bg-[#DCD5C6]" />

              <FilesSection files={files} uploading={uploadingFiles}
                onUpload={handleUploadFile} onDelete={handleDeleteFile} onDownload={handleDownloadFile}
                fileInputRef={docInputRef} />

              <Separator className="bg-[#DCD5C6]" />

              {/* Bottom actions: Archive + Close */}
              <div className="flex flex-col gap-3 pb-4">
                <Button onClick={handleMarkComplete}
                  variant="outline"
                  className="w-full border-[#4A9D6E] text-[#2C4C3B] hover:bg-[#4A9D6E] hover:text-white rounded-xl"
                  data-testid="archive-project-button">
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Project
                </Button>
                <Button onClick={onClose}
                  variant="outline"
                  className="w-full border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
                  data-testid="close-detail-button">
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      <PhotoPreviewDialog photo={previewPhoto} blobUrl={previewPhoto ? photoBlobs[previewPhoto.id] : null}
        onClose={() => setPreviewPhoto(null)} />
    </>
  );
}
